import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container, Typography, Box, Paper, Grid, Chip, Divider, Button, Alert, useMediaQuery,
    CircularProgress, IconButton, Dialog, DialogContent, Rating, Avatar, Tabs, Tab,
    Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Business } from '../types/business';
import { businessService } from '../services/businessService';
import { useAuth } from '../context/AuthContext';
import { isBusinessOpen } from '../utils/businessUtils';
import { socialUrl, websiteUrl } from '../utils/socialLinks';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DirectionsIcon from '@mui/icons-material/Directions';
import StarIcon from '@mui/icons-material/Star';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TelegramIcon from '@mui/icons-material/Telegram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import WheelchairPickupIcon from '@mui/icons-material/WheelchairPickup';
import PaymentIcon from '@mui/icons-material/Payment';
import TimerIcon from '@mui/icons-material/Timer';
import VerifiedIcon from '@mui/icons-material/Verified';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OwnershipBadge, { ownershipOf } from '../components/common/OwnershipBadge';
import { isIranBusiness, IRAN_COLOR, IRAN_TAG_LABEL } from '../utils/iranBusiness';
import { FEATURED_LABEL, FEATURED_COLOR, PREMIUM_LABEL, PREMIUM_COLOR } from '../utils/featured';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import PolicyIcon from '@mui/icons-material/Policy';
import CommentsSection from '../components/CommentsSection';
import GoogleReviews from '../components/GoogleReviews';
import { schemaTypeForCategory, openingHoursSpecification, faqItemsOf, faqPageSchema } from '../utils/businessSchema';
import BusinessCard from '../components/common/BusinessCard';
import useSEO from '../hooks/useSEO';
import { API_ORIGIN } from '../config/api';

const BACKEND_URL = API_ORIGIN;

const isAbsoluteMediaUrl = (url: string) =>
    url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:') || url.startsWith('blob:');

const resolveMediaUrl = (url?: string) => {
    if (!url) return '';
    if (isAbsoluteMediaUrl(url)) return url;
    return `${BACKEND_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

const stripHtmlForDescription = (html: string, maxLength = 160) => {
    const text = String(html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength - 1).trim()}\u2026`;
};

const hasPersianText = (value: string) => /[\u0600-\u06ff]/.test(value || '');
const textDirectionFor = (value: string): 'rtl' | 'ltr' => hasPersianText(value) ? 'rtl' : 'ltr';
const textAlignFor = (value: string): 'right' | 'left' => hasPersianText(value) ? 'right' : 'left';
const escapeHtmlText = (value: string) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
const descriptionToHtmlParagraphs = (value: string) => {
    const text = String(value || '').trim();
    if (!text) return '';
    if (/<(?:p|div|br|ul|ol|li|h[1-6])[^>]*>/i.test(text)) return text;
    return escapeHtmlText(text).replace(/\n/g, '<br>');
};

const latestBusinessUpdateDate = (business: Business) => {
    const candidates = [
        business.lastOwnerUpdatedAt,
        business.lastImportedAt,
        business.lastScannedAt,
        business.googleDataImportedAt,
        business.updatedAt,
    ].filter(Boolean) as string[];
    const latest = candidates
        .map(value => new Date(value))
        .filter(date => !Number.isNaN(date.getTime()))
        .sort((a, b) => b.getTime() - a.getTime())[0];
    return latest || null;
};

const usableGoogleMapsUri = (value?: string) => {
    const uri = String(value || '').trim();
    return /^https?:\/\//i.test(uri) ? uri : '';
};

interface BusinessDetailsPageProps {
    previewBusiness?: Business;
    onExitPreview?: () => void;
}

const BusinessDetailsPage: React.FC<BusinessDetailsPageProps> = ({ previewBusiness, onExitPreview }) => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isPreviewMode = Boolean(previewBusiness);
    const [business, setBusiness] = useState<Business | null>(previewBusiness || null);
    const [similarBusinesses, setSimilarBusinesses] = useState<Business[]>([]);
    const [loading, setLoading] = useState(!previewBusiness);
    const [error, setError] = useState<string | null>(null);
    const [lightboxImage, setLightboxImage] = useState<string | null>(null);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [likesCount, setLikesCount] = useState(0);
    const [isLiked, setIsLiked] = useState(false);
    const [descriptionTab, setDescriptionTab] = useState<'persian' | 'english'>('persian');
    // Where the desktop photo gallery renders is decided at runtime, not fixed:
    // if the description is short, the left column ends well before the
    // sidebar does, leaving an awkward empty gap - so the gallery goes there
    // instead, right under the description. If the description is tall enough
    // to already run past the sidebar's map+contact+Follow Us block, the
    // gallery stays in the sidebar (right under Follow Us) instead of pushing
    // further down past Working Hours. Measured with ResizeObserver rather
    // than computed once, since description height depends on the fa/en tab
    // toggle and image loads change both columns' heights after first paint.
    const descriptionRef = useRef<HTMLDivElement | null>(null);
    // On mobile the two columns stack, so comments (left column) would land
    // above the map/contact/Working Hours boxes. Below md they're rendered
    // at the end of the right column instead, after Working Hours.
    const isMdUp = useMediaQuery('(min-width:900px)');
    const sidebarTopRef = useRef<HTMLDivElement | null>(null);
    // Tracks which route id/slug the CURRENT `business` state was actually
    // fetched for. Needed by the slug-canonicalization effect below: when you
    // navigate from one business's page to another (e.g. a similar-listing
    // card), the `id` route param updates immediately, but `business` in state
    // is still the OLD business until the new fetch resolves. Without this,
    // that one render in between has `id` = new business's slug and
    // `business.slug` = old business's slug, which look like a mismatch and
    // fire an unwanted redirect back to the old business's URL.
    const fetchedForIdRef = useRef<string | null>(null);
    // Each fetch gets a number; only the newest one may update the page, so a slow
    // earlier request (or its failure) can never overwrite the business that is
    // now showing.
    const fetchSeqRef = useRef(0);
    // When an old UUID link is swapped for the slug address, the business is
    // already loaded - this lets the fetch effect skip asking for it a second time.
    const skipFetchForRef = useRef<string | null>(null);
    const [gallerySlot, setGallerySlot] = useState<'left' | 'sidebar'>('sidebar');

    useEffect(() => {
        let cancelled = false;
        let observer: ResizeObserver | null = null;
        // Refs can still be null on the very first effect pass right after a
        // client-side nav (e.g. clicking a similar-listing card) before the
        // new tree has fully committed - retry across a few frames instead of
        // silently giving up and leaving the gallery stuck in the last slot.
        const attach = (attempt = 0) => {
            if (cancelled) return;
            const descriptionEl = descriptionRef.current;
            const sidebarTopEl = sidebarTopRef.current;
            if (!descriptionEl || !sidebarTopEl) {
                if (attempt > 20) return;
                requestAnimationFrame(() => attach(attempt + 1));
                return;
            }
            const recompute = () => {
                setGallerySlot(descriptionEl.offsetHeight < sidebarTopEl.offsetHeight ? 'left' : 'sidebar');
            };
            recompute();
            observer = new ResizeObserver(recompute);
            observer.observe(descriptionEl);
            observer.observe(sidebarTopEl);
        };
        attach();
        return () => {
            cancelled = true;
            observer?.disconnect();
        };
    }, [descriptionTab, business?.id]);

    const handleImageClick = (url: string, index = 0) => {
        setLightboxImage(url);
        setLightboxIndex(index);
    };

    useEffect(() => {
        if (isPreviewMode) return;
        if (!id) return;
        if (skipFetchForRef.current === id) {
            skipFetchForRef.current = null;
            fetchedForIdRef.current = id;
            return;
        }
        fetchBusiness(id);
    }, [id, isPreviewMode]);

    // Keep the preview in sync with the live draft object passed down from the
    // register/edit form as the user keeps typing.
    useEffect(() => {
        if (previewBusiness) {
            setBusiness(previewBusiness);
        }
    }, [previewBusiness]);

    // Business URLs used to be raw UUIDs (/business/<id>). Now that navigation elsewhere
    // in the app uses the human-readable slug, canonicalize any old UUID link (or a
    // stale slug after a rename) to the current slug so search engines converge on one
    // URL per business instead of splitting ranking signal across two.
    useEffect(() => {
        if (isPreviewMode) return;
        if (business && id && business.slug && id !== business.slug && fetchedForIdRef.current === id) {
            skipFetchForRef.current = business.slug;
            navigate(`/business/${business.slug}`, { replace: true });
        }
    }, [business, id, navigate, isPreviewMode]);

    const seoCategoryLabel = business?.category?.name || '';
    const seoCityLabel = business?.city?.name || business?.customCity || '';
    const seoTitle = business
        ? `${business.name}${seoCategoryLabel ? ' - ' + seoCategoryLabel : ''}${seoCityLabel ? ' | ' + seoCityLabel : ''}`
        : 'در حال بارگذاری...';
    const seoDescription = business ? (stripHtmlForDescription(business.description) || undefined) : undefined;
    // Link previews do not take SVG (a website illustration used as the header): use the logo then.
    const seoImageRaw = (business?.mainImage && !/\.svg(?:$|[?#])/i.test(business.mainImage) ? business.mainImage : '') || business?.logoUrl || '';
    const seoImage = seoImageRaw ? resolveMediaUrl(seoImageRaw) : undefined;
    const seoCanonicalPath = business ? `/business/${business.slug || business.id}` : undefined;
    const seoOpeningHours = business ? openingHoursSpecification(business.workingHours) : [];
    const seoFaq = business ? faqItemsOf(business.additionalInfo) : [];
    const seoBusinessJsonLd = business ? {
        '@context': 'https://schema.org',
        '@type': schemaTypeForCategory(business.category?.slug),
        name: business.name,
        ...(seoCanonicalPath ? { url: `https://cheesta.com.au${seoCanonicalPath}` } : {}),
        ...(seoOpeningHours.length ? { openingHoursSpecification: seoOpeningHours } : {}),
        ...(seoDescription ? { description: seoDescription } : {}),
        ...(seoImage ? { image: [seoImage] } : {}),
        address: {
            '@type': 'PostalAddress',
            streetAddress: business.address,
            ...(seoCityLabel ? { addressLocality: seoCityLabel } : {}),
            addressCountry: business.country === 'Iran' ? 'IR' : 'AU',
        },
        ...(business.phone ? { telephone: business.phone } : {}),
        ...(business.companyEmail ? { email: business.companyEmail } : {}),
        ...(business.website ? { sameAs: [business.website] } : {}),
        ...(business.latitude != null && business.longitude != null ? {
            geo: { '@type': 'GeoCoordinates', latitude: business.latitude, longitude: business.longitude },
        } : {}),
        // Only rated reviews count; questions are not reviews.
        ...(business.ratingAverage && business.reviewCount ? {
            aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: business.ratingAverage,
                reviewCount: business.reviewCount,
            },
        } : {}),
    } : null;

    // The business, plus its FAQ as an FAQPage when it has one.
    const seoFaqJsonLd = faqPageSchema(seoFaq, seoCanonicalPath ? `https://cheesta.com.au${seoCanonicalPath}` : undefined);
    const seoJsonLd = seoBusinessJsonLd && seoFaqJsonLd ? [seoBusinessJsonLd, seoFaqJsonLd] : seoBusinessJsonLd;

    useSEO({
        title: seoTitle,
        description: seoDescription,
        canonical: seoCanonicalPath,
        image: seoImage,
        type: 'business.business',
        jsonLd: seoJsonLd,
        enabled: !isPreviewMode,
    });

    const fetchBusiness = async (businessId: string) => {
        const seq = ++fetchSeqRef.current;
        const isCurrent = () => seq === fetchSeqRef.current;
        setLoading(true);
        setError(null);
        let data: any;
        try {
            try {
                data = await businessService.getById(businessId);
            } catch (firstErr: any) {
                // A dropped connection or gateway hiccup (no reply, or a 502/503/504)
                // is usually gone a moment later - the same reason a manual refresh
                // worked - so try once more before showing an error.
                const status = firstErr?.response?.status;
                if (firstErr?.response && ![502, 503, 504].includes(status)) throw firstErr;
                await new Promise(r => setTimeout(r, 800));
                if (!isCurrent()) return;
                data = await businessService.getById(businessId);
            }
            if (!isCurrent()) return;
            setBusiness(data);
            fetchedForIdRef.current = businessId;
            setLikesCount(data.likes || 0);
            setIsLiked(data.isLiked || false);
        } catch (err: any) {
            if (!isCurrent()) return;
            console.error('Failed to load business', businessId, err);
            setError(err.response?.data?.error || 'Failed to load business details');
            return;
        } finally {
            if (isCurrent()) setLoading(false);
        }

        // Similar businesses (same category) are extra. If this lookup fails the
        // listing itself still shows - it used to replace the whole page with
        // "Failed to load business details".
        if (data?.categoryId) {
            try {
                const similar = await businessService.getAll(undefined, data.categoryId, undefined, 5);
                if (!isCurrent()) return;
                // Compare against data.id (the real UUID), not the URL param, which
                // is usually the slug.
                setSimilarBusinesses(similar.filter((b: Business) => b.id !== data.id).slice(0, 4));
            } catch (err) {
                console.warn('Similar businesses could not be loaded', err);
            }
        }
    };

    const handleLikeClick = async () => {
        if (!business || isPreviewMode) return;
        try {
            const result = await businessService.likeBusiness(business.id);
            if (result.success) {
                setIsLiked(result.isLiked);
                setLikesCount(prev => result.isLiked ? prev + 1 : prev - 1);
            }
        } catch (err) {
            console.error('Failed to like business:', err);
        }
    };

    const handleStatusUpdate = async (newStatus: 'approved' | 'rejected') => {
        if (!business || !user || user.role !== 'admin' || isPreviewMode) return;
        if (window.confirm(`Are you sure you want to ${newStatus} this business?`)) {
            try {
                await businessService.updateStatus(business.id, newStatus, user.email);
                fetchBusiness(business.id); // Refresh
            } catch (err: any) {
                alert(err.response?.data?.error || 'Operation failed');
            }
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    if (error) return (
        <Container sx={{ mt: 4 }}>
            <Alert severity="error" action={id ? <Button color="inherit" size="small" onClick={() => fetchBusiness(id)}>Try again</Button> : undefined}>
                {error}
            </Alert>
        </Container>
    );
    if (!business) return <Container sx={{ mt: 4 }}><Alert severity="info">Business not found</Alert></Container>;

    const isAdmin = user?.role === 'admin';
    const backendUrl = API_ORIGIN;
    const googleMapsUrl = usableGoogleMapsUri(business.googleMapsUri);
    const googleMapsNotFound = String(business.googleMapsUri || '').trim().toLowerCase() === 'not found';

    const getImageUrl = (url?: string) => {
        if (!url) return undefined;
        if (isAbsoluteMediaUrl(url)) {
            return url;
        }
        let cleaned = url.replace(/\\/g, '/');
        cleaned = cleaned.startsWith('/') ? cleaned : '/' + cleaned;
        return `${backendUrl}${cleaned}`;
    };

    const heroImageUrl = getImageUrl(business.mainImage) || getImageUrl(business.logoUrl) || '/placeholder-business.jpg';

    const isFieldEmpty = (field: any) => {
        if (!field) return true;
        if (typeof field === 'string') {
            const trimmed = field.trim();
            return !trimmed || trimmed === '[]' || trimmed === '{}' || trimmed === 'null';
        }
        if (typeof field === 'object') {
            return Object.keys(field).length === 0;
        }
        return false;
    };

    let parsedAdditionalInfo: any = {};
    if (business.additionalInfo) {
        if (typeof business.additionalInfo === 'string') {
            try {
                parsedAdditionalInfo = JSON.parse(business.additionalInfo);
            } catch (e) {}
        } else if (typeof business.additionalInfo === 'object') {
            parsedAdditionalInfo = business.additionalInfo;
        }
    }
    
    const services: string[] = Array.isArray(parsedAdditionalInfo.services) ? parsedAdditionalInfo.services : [];
    const servicePrices: string[] = Array.isArray(parsedAdditionalInfo.servicePrices) ? parsedAdditionalInfo.servicePrices : [];
    const features: string[] = Array.isArray(parsedAdditionalInfo.features) ? parsedAdditionalInfo.features : [];
    const policies: string[] = Array.isArray(parsedAdditionalInfo.policies)
        ? parsedAdditionalInfo.policies.map((item: any) => String(item || '').trim()).filter(Boolean)
        : [];
    const otherUsefulInfo: string[] = Array.isArray(parsedAdditionalInfo.otherUsefulInfo)
        ? parsedAdditionalInfo.otherUsefulInfo.map((item: any) => String(item || '').trim()).filter(Boolean)
        : [];
    const faqItems = faqItemsOf(parsedAdditionalInfo);
    const englishDescription = typeof parsedAdditionalInfo.englishDescription === 'string'
        ? parsedAdditionalInfo.englishDescription.trim()
        : '';
    const persianDescriptionTranslated = Boolean(parsedAdditionalInfo.persianDescriptionTranslated);
    const keyPeople = Array.isArray(business.keyPeople) && business.keyPeople.length > 0
        ? business.keyPeople
        : business.keyPerson
            ? [{ name: business.keyPerson, role: '' }]
            : [];
    const googleServiceOptions = typeof business.googleServiceOptions === 'string'
        ? (() => {
            try { return JSON.parse(business.googleServiceOptions as any); } catch { return null; }
        })()
        : business.googleServiceOptions;
    const googleServiceOptionLabels: string[] = Array.isArray(googleServiceOptions?.options)
        ? googleServiceOptions.options.map((item: any) => String(item || '').trim()).filter(Boolean)
        : [];
    let galleryArr: string[] = [];
    if (typeof business.galleryUrls === 'string') {
        try { galleryArr = JSON.parse(business.galleryUrls); }
        catch (e) { galleryArr = [business.galleryUrls]; }
    } else if (Array.isArray(business.galleryUrls)) {
        galleryArr = business.galleryUrls;
    }
    // The main photo is already shown in the page header, so the gallery only
    // repeats it when the listing has no gallery photos of its own.
    const galleryPhotos = galleryArr.filter(Boolean);
    const businessPhotoUrls = Array.from(new Set(
        (galleryPhotos.length > 0 ? galleryPhotos : (business.mainImage ? [business.mainImage] : []))
            .map(u => getImageUrl(u) as string)
            .filter(Boolean)
    ));
    const showPreviousPhoto = () => {
        if (!businessPhotoUrls.length) return;
        const nextIndex = (lightboxIndex - 1 + businessPhotoUrls.length) % businessPhotoUrls.length;
        setLightboxIndex(nextIndex);
        setLightboxImage(businessPhotoUrls[nextIndex]);
    };
    const showNextPhoto = () => {
        if (!businessPhotoUrls.length) return;
        const nextIndex = (lightboxIndex + 1) % businessPhotoUrls.length;
        setLightboxIndex(nextIndex);
        setLightboxImage(businessPhotoUrls[nextIndex]);
    };

    const galleryBrowser = (sticky = false) => (
        <Paper
            sx={{
                ...sidebarPaperStyle,
                ...(sticky ? { position: 'sticky', top: 96 } : {}),
            }}
        >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2854a1' }}>
                Photo Gallery
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box
                sx={{
                    position: 'relative',
                    mb: 1.5,
                    width: '100%',
                    aspectRatio: '4 / 3',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    bgcolor: '#111827',
                    cursor: 'pointer',
                }}
                onClick={() => handleImageClick(businessPhotoUrls[0], 0)}
            >
                {/* Same fit-entire-photo treatment as the hero banner and listing
                    cards: show the whole photo (object-fit: contain) over a
                    blurred fill of itself instead of cropping to fill the 4:3 box. */}
                <Box
                    component="img"
                    src={businessPhotoUrls[0]}
                    alt=""
                    aria-hidden="true"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        filter: 'blur(18px) brightness(0.55)',
                        transform: 'scale(1.15)',
                    }}
                />
                <Box
                    component="img"
                    src={businessPhotoUrls[0]}
                    alt={`${business.name} photo`}
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block',
                    }}
                />
                {businessPhotoUrls.length > 1 && (
                    <Chip
                        label={`${businessPhotoUrls.length} photos`}
                        size="small"
                        sx={{ position: 'absolute', right: 8, bottom: 8, bgcolor: 'rgba(0,0,0,0.58)', color: 'white', borderRadius: '4px' }}
                    />
                )}
            </Box>
            {businessPhotoUrls.length > 1 && (
                <>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                        <Button
                            fullWidth
                            size="small"
                            variant="outlined"
                            startIcon={<ChevronLeftIcon />}
                            onClick={() => handleImageClick(businessPhotoUrls[businessPhotoUrls.length - 1], businessPhotoUrls.length - 1)}
                        >
                            Previous
                        </Button>
                        <Button
                            fullWidth
                            size="small"
                            variant="outlined"
                            endIcon={<ChevronRightIcon />}
                            onClick={() => handleImageClick(businessPhotoUrls[1], 1)}
                        >
                            Next
                        </Button>
                    </Box>
                    <Grid container spacing={1}>
                        {businessPhotoUrls.slice(0, 12).map((url, index) => (
                            <Grid key={url} size={{ xs: 3 }}>
                                <Box
                                    component="img"
                                    src={url}
                                    alt={`Gallery ${index + 1}`}
                                    onClick={() => handleImageClick(url, index)}
                                    sx={{
                                        width: '100%',
                                        aspectRatio: '1 / 1',
                                        objectFit: 'cover',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        border: '1px solid rgba(0,0,0,0.08)',
                                    }}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}
        </Paper>
    );

    const sectionPaperStyle = {
        p: 3,
        mb: 3,
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        border: '1px solid rgba(0,0,0,0.08)',
        bgcolor: '#ffffff',
        boxSizing: 'border-box',
        '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
        }
    };

    const sidebarPaperStyle = {
        p: 2.5,
        mb: 3,
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        border: '1px solid rgba(0,0,0,0.08)',
        bgcolor: '#ffffff',
        boxSizing: 'border-box',
        '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
        }
    };

    const getAmenityIcon = (amenity: string) => {
        const lowerAmenity = amenity.toLowerCase();
        if (lowerAmenity.includes('credit card')) return <CreditCardIcon sx={{ fontSize: '1.1rem' }} />;
        if (lowerAmenity.includes('delivery')) return <LocalShippingIcon sx={{ fontSize: '1.1rem' }} />;
        if (lowerAmenity.includes('parking')) return <LocalParkingIcon sx={{ fontSize: '1.1rem' }} />;
        if (lowerAmenity.includes('reservation')) return <EventAvailableIcon sx={{ fontSize: '1.1rem' }} />;
        if (lowerAmenity.includes('wheelchair')) return <WheelchairPickupIcon sx={{ fontSize: '1.1rem' }} />;
        if (lowerAmenity.includes('online payment')) return <PaymentIcon sx={{ fontSize: '1.1rem' }} />;
        if (lowerAmenity.includes('24x7') || lowerAmenity.includes('24/7') || lowerAmenity.includes('after hours')) return <TimerIcon sx={{ fontSize: '1.1rem' }} />;
        if (lowerAmenity.includes('verified') || lowerAmenity.includes('claim')) return <VerifiedIcon sx={{ fontSize: '1.1rem' }} />;
        return <CheckCircleIcon sx={{ fontSize: '1.1rem' }} />;
    };

    // Rendered in exactly one place (see isMdUp) so CommentsSection only
    // mounts - and fetches - once.
    const commentsBlock = isPreviewMode ? (
        <Paper sx={{ ...sectionPaperStyle, textAlign: 'center', color: 'text.secondary' }}>
            Reviews will appear here once this listing is live.
        </Paper>
    ) : (
        <>
            {(business.googlePlaceId || (business as any).googleMapsUri) && <GoogleReviews businessId={business.id} paperSx={sectionPaperStyle} />}
            <CommentsSection businessId={business.id} ownerEmail={(business as any).owner?.email || business.companyEmail} questionsEnabled={Boolean(String(business.companyEmail || '').trim())} />
        </>
    );

    return (
        <Box sx={{ pb: 8 }}>
            {/* Top Navigation Bar - only shown in preview mode (need a way back to
                the editor there); for a normal visit this used to sit above the
                hero as a plain "Back" button, pushing the whole page down and
                forcing a scroll just to see the business name/photo. Removed for
                the normal case so the hero is the first thing on the page. */}
            {isPreviewMode && (
                <Container sx={{ mt: 2, mb: 2, maxWidth: '1400px !important', px: 3, mx: 'auto' }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => onExitPreview?.()}
                    >
                        Back to editing
                    </Button>
                </Container>
            )}

            {isPreviewMode && (
                <Container sx={{ mb: 3, maxWidth: '1400px !important', px: 3, mx: 'auto' }}>
                    <Alert severity="info" sx={{ borderRadius: '8px' }}>
                        Preview only - this listing has not been submitted yet. This is roughly how it will look once approved and live.
                    </Alert>
                </Container>
            )}

            {/* Ownership: shown in the header under the name (badge + "this is my business"),
                so nothing pushes the listing down here. */}

            {/* Admin Controls - only worth showing when there's something to act
                on (pending/rejected). Approved is the normal, permanent state for
                the vast majority of listings, so surfacing a static "APPROVED"
                chip here on every single visit was just clutter pushing the
                actual page content down for no benefit. */}
            {isAdmin && !isPreviewMode && business.status !== 'approved' && (
                <Container sx={{ mb: 3, maxWidth: '1400px !important', px: 3, mx: 'auto' }}>
                    <Paper sx={{ p: 2, bgcolor: '#f5f5f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e0e0e0', elevation: 0 }}>
                        <Box>
                            <Typography variant="subtitle2" component="span" sx={{ mr: 1 }}>Status:</Typography>
                            <Chip
                                label={business.status.toUpperCase()}
                                color={business.status === 'rejected' ? 'error' : 'warning'}
                                size="small"
                            />
                        </Box>
                        <Box>
                            {business.status === 'pending' && (
                                <>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        startIcon={<CheckCircleIcon />}
                                        onClick={() => handleStatusUpdate('approved')}
                                        sx={{ mr: 1 }}
                                    >
                                        Approve
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        startIcon={<CancelIcon />}
                                        onClick={() => handleStatusUpdate('rejected')}
                                    >
                                        Reject
                                    </Button>
                                </>
                            )}
                        </Box>
                    </Paper>
                </Container>
            )}

            {/* Hero Banner Section */}
            {/*
             * Cropping this to cover a full-bleed width (previously
             * `backgroundSize: cover`) regularly cut heads/shoulders off
             * whatever photo came out of the WordPress migration, the same
             * problem the listing cards had. Same fix as BusinessCard: show
             * the ENTIRE photo (object-fit: contain, nothing cropped) over a
             * blurred, darkened copy of itself filling the rest of the
             * banner, instead of cropping to fill the width.
             */}
            <Box
                sx={{
                    position: 'relative',
                    // minHeight, not height: on narrow screens the logo + name +
                    // address stack is taller than the banner, and a fixed height
                    // cut the logo and business name off the top.
                    minHeight: { xs: 220, sm: 300, md: 380, lg: 440 },
                    width: '100%',
                    overflow: 'hidden',
                    bgcolor: '#111827',
                    mb: 4,
                    display: 'flex',
                    alignItems: 'flex-end',
                    color: 'white'
                }}
            >
                <Box
                    component="img"
                    src={heroImageUrl}
                    alt=""
                    aria-hidden="true"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        filter: 'blur(24px) brightness(0.5)',
                        transform: 'scale(1.15)',
                    }}
                />
                <Box
                    component="img"
                    src={heroImageUrl}
                    alt={business.name}
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(rgba(0,0,0,0.15), rgba(0,0,0,0.55))',
                    }}
                />
                <Container sx={{ pt: 3, pb: { xs: 2.5, md: 4 }, maxWidth: '1400px !important', px: { xs: 2, md: 3 }, mx: 'auto', position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Avatar
                            src={getImageUrl(business.logoUrl)}
                            sx={{
                                width: { xs: 84, md: 140 },
                                height: { xs: 84, md: 140 },
                                border: { xs: '3px solid white', md: '5px solid white' },
                                bgcolor: 'white',
                                boxShadow: 3,
                                mb: 1.5,
                                // Show the whole logo inside the circle: wide wordmarks ("Banoo") were
                                // cropped to the middle letters by the default "cover" fit.
                                '& img': { objectFit: 'contain', p: { xs: '10%', md: '12%' }, boxSizing: 'border-box' },
                            }}
                        >
                            {!business.logoUrl && <BusinessIcon sx={{ fontSize: 80, color: 'grey.400' }} />}
                        </Avatar>

                        <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '1.4rem', md: '1.75rem' }, lineHeight: { xs: 1.5, md: 2.4 }, textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
                            {business.name}
                        </Typography>
                        {(isIranBusiness(business) || business.isFeatured || business.isPremium) && (
                            <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {(business.isFeatured || business.isPremium) && (
                                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, bgcolor: business.isPremium ? PREMIUM_COLOR : FEATURED_COLOR, color: '#fff', borderRadius: '6px', px: 1.25, py: 0.4, fontSize: 13, fontWeight: 700 }}>
                                        {business.isPremium ? <WorkspacePremiumIcon sx={{ fontSize: 15 }} /> : <RocketLaunchIcon sx={{ fontSize: 15 }} />}
                                        {business.isPremium ? PREMIUM_LABEL : FEATURED_LABEL}
                                    </Box>
                                )}
                                {isIranBusiness(business) && (
                                    <Box component="span" sx={{ display: 'inline-block', bgcolor: IRAN_COLOR, color: '#fff', borderRadius: '6px', px: 1.25, py: 0.4, fontSize: 13, fontWeight: 700 }}>
                                        {IRAN_TAG_LABEL}
                                    </Box>
                                )}
                            </Box>
                        )}
                        {!isPreviewMode && business.status === 'approved' && ownershipOf(business) && (
                            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <OwnershipBadge business={business} />
                                {ownershipOf(business) === 'auto' && (
                                    <Button
                                        size="small" variant="outlined"
                                        onClick={() => navigate(business.website ? `/user/website-scan?claim=${business.id}` : '/contact')}
                                        sx={{ height: 24, py: 0, px: 1.25, fontSize: 12, color: '#fff', borderColor: 'rgba(255,255,255,0.7)', bgcolor: 'rgba(0,0,0,0.25)', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(0,0,0,0.4)' } }}
                                    >
                                        این کسب‌وکار من است
                                    </Button>
                                )}
                            </Box>
                        )}

                        <Typography variant="h6" sx={{ mb: 1, fontSize: { xs: '0.9rem', md: '1rem' }, lineHeight: { xs: 1.6, md: 2.6 }, opacity: 0.9, textAlign: 'left', direction: 'ltr', width: '100%' }}>
                            {business.address}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            {business.category && (
                                <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 2.6 }}>
                                    {business.category.name}
                                </Typography>
                            )}
                            {business.city && (
                                <Typography variant="body1" sx={{ lineHeight: 2.6 }}>
                                    {business.city.name}
                                </Typography>
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            {business.reviewCount ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Rating 
                                        value={business.ratingAverage || 0} 
                                        readOnly 
                                        size="small" 
                                        precision={0.5}
                                        sx={{ color: '#faaf00' }} 
                                        emptyIcon={<StarIcon style={{ opacity: 0.55, color: 'white' }} fontSize="inherit" />} 
                                    />
                                    <Typography variant="body2" sx={{ ml: 1 }}>
                                        {business.ratingAverage?.toFixed(1)} ({business.reviewCount} Cheesta {business.reviewCount === 1 ? 'Review' : 'Reviews'})
                                    </Typography>
                                </Box>
                            ) : (
                                <Typography variant="body2" sx={{ color: 'white', opacity: 0.9 }}>0 Cheesta Reviews</Typography>
                            )}

                            {business.googlePlaceId && business.googleRating && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.1)', px: 1.5, py: 0.5, borderRadius: 1.5, border: '1px solid rgba(255,255,255,0.2)' }}>
                                    <Box component="img" src="https://www.gstatic.com/images/branding/product/1x/googleg_32dp.png" sx={{ height: 16 }} />
                                    <Rating 
                                        value={business.googleRating} 
                                        readOnly 
                                        size="small" 
                                        precision={0.1}
                                        sx={{ color: '#faaf00', fontSize: '1rem' }} 
                                        emptyIcon={<StarIcon style={{ opacity: 0.55, color: 'white' }} fontSize="inherit" />} 
                                    />
                                    <Typography variant="body2" sx={{ ml: 0.5, fontWeight: 'bold', color: 'white' }}>
                                        {business.googleRating.toFixed(1)} ({business.googleReviewCount} Google reviews)
                                    </Typography>
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {business.workingHours && Object.keys(business.workingHours).length > 0 && (
                                    <Chip
                                        label={isBusinessOpen(business) ? 'Now Open' : 'Closed Now'}
                                        size="small"
                                        sx={{
                                            bgcolor: isBusinessOpen(business) ? '#2e7d32' : '#d32f2f',
                                            color: 'white',
                                            fontWeight: 'bold',
                                            height: '24px'
                                        }}
                                    />
                                )}
                                {latestBusinessUpdateDate(business) && (
                                    <Chip
                                        label={`Updated ${latestBusinessUpdateDate(business)?.toLocaleDateString()}`}
                                        size="small"
                                        sx={{
                                            bgcolor: 'rgba(255,255,255,0.12)',
                                            color: 'white',
                                            border: '1px solid rgba(255,255,255,0.25)',
                                            height: '24px'
                                        }}
                                    />
                                )}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 0.5,
                                        color: (business.views || 0) > 0 ? '#e2e8f0' : 'white'
                                    }}>
                                        <VisibilityIcon sx={{ fontSize: 20 }} />
                                        <Typography variant="body2">{business.views || 0}</Typography>
                                    </Box>
                                    <Box 
                                        onClick={handleLikeClick}
                                        sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 0.5, 
                                            cursor: 'pointer',
                                            color: (likesCount > 0 || isLiked) ? '#93c5fd' : 'white',
                                            '&:hover': { opacity: 0.8 }
                                        }}
                                    >
                                        {isLiked ? <ThumbUpIcon sx={{ fontSize: 20 }} /> : <ThumbUpOutlinedIcon sx={{ fontSize: 20 }} />}
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{likesCount}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Main Content Sections */}
            <Container sx={{ mt: 4, mb: 10, maxWidth: '1400px !important', width: '100%', px: 3, mx: 'auto' }}>
                <Grid container spacing={3}>
                    {/* Left Column: Description & Gallery */}
                    <Grid size={{ xs: 12, md: 7 }}>
                        {/* About Box */}
                        <Paper sx={sectionPaperStyle} ref={descriptionRef}>
                            {englishDescription && (
                                <Tabs
                                    value={descriptionTab}
                                    onChange={(_, value) => setDescriptionTab(value)}
                                    sx={{
                                        mb: 2,
                                        direction: 'rtl',
                                        minHeight: 36,
                                        borderBottom: '1px solid #eef0f4',
                                        '& .MuiTab-root': { minHeight: 36, fontSize: '13px', fontWeight: 700 }
                                    }}
                                >
                                    <Tab value="persian" label="فارسی" />
                                    <Tab value="english" label="English" />
                                </Tabs>
                            )}
                            {persianDescriptionTranslated && descriptionTab === 'persian' && (
                                <Alert
                                    severity="info"
                                    icon={false}
                                    sx={{ mb: 2, py: 0.5, direction: 'rtl', textAlign: 'right', fontSize: '13px' }}
                                >
                                    <Chip label="ترجمه شده" size="small" color="info" sx={{ ml: 1, fontSize: '12px', height: 22 }} />
                                    این توضیح از متن انگلیسی ترجمه شده است.
                                </Alert>
                            )}
                            {/* Both language versions are always rendered (never conditionally
                                omitted) and toggled with CSS display instead of JS-only
                                swapping, so search engines can index whichever language
                                isn't the active tab too — a client-only swap would leave
                                it out of the DOM entirely and unindexable. */}
                            <Box
                                sx={{
                                    display: descriptionTab === 'persian' ? 'block' : 'none',
                                    '& p': { mb: 2 },
                                    lineHeight: 1.7,
                                    color: '#717a8f',
                                    fontSize: '14px',
                                    textAlign: textAlignFor(business.description),
                                    direction: textDirectionFor(business.description)
                                }}
                                dangerouslySetInnerHTML={{ __html: descriptionToHtmlParagraphs(business.description) }}
                            />
                            {englishDescription && (
                                <Box
                                    sx={{
                                        display: descriptionTab === 'english' ? 'block' : 'none',
                                        '& p': { mb: 2 },
                                        lineHeight: 1.7,
                                        color: '#717a8f',
                                        fontSize: '14px',
                                        textAlign: textAlignFor(englishDescription),
                                        direction: textDirectionFor(englishDescription)
                                    }}
                                    dangerouslySetInnerHTML={{ __html: descriptionToHtmlParagraphs(englishDescription) }}
                                />
                            )}
                        </Paper>

                        {gallerySlot === 'left' && businessPhotoUrls.length > 0 && (
                            <Box sx={{ display: { xs: 'none', md: 'block' }, mb: 3 }}>
                                {galleryBrowser(false)}
                            </Box>
                        )}


                        {(business.googleEditorialSummary || googleServiceOptionLabels.length > 0) && (
                            <Paper sx={sectionPaperStyle}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2, color: '#2854a1', fontSize: '1.1rem' }}>
                                    اطلاعات دریافت‌شده از گوگل
                                </Typography>
                                {business.googleEditorialSummary && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: '#4b5563',
                                            lineHeight: 1.7,
                                            mb: googleServiceOptionLabels.length > 0 ? 2 : 0,
                                            textAlign: textAlignFor(business.googleEditorialSummary),
                                            direction: textDirectionFor(business.googleEditorialSummary)
                                        }}
                                    >
                                        {business.googleEditorialSummary}
                                    </Typography>
                                )}
                                {googleServiceOptionLabels.length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {googleServiceOptionLabels.map((option) => (
                                            <Chip
                                                key={option}
                                                label={option}
                                                icon={getAmenityIcon(option)}
                                                variant="outlined"
                                                sx={{
                                                    borderRadius: '8px',
                                                    borderColor: 'rgba(40, 84, 161, 0.2)',
                                                    bgcolor: 'rgba(40, 84, 161, 0.02)',
                                                    color: '#2854a1',
                                                    '& .MuiChip-icon': { color: '#2854a1' },
                                                }}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Paper>
                        )}

                        {/* Amenities Box */}
                        {business.amenities && business.amenities.length > 0 && (
                            <Paper sx={sectionPaperStyle}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2, color: '#2854a1', fontSize: '1.1rem' }}>
                                    Business Services
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                    {business.amenities.map((amenity, index) => (
                                        <Chip 
                                            key={index} 
                                            label={amenity} 
                                            icon={getAmenityIcon(amenity)}
                                            variant="outlined" 
                                            sx={{ 
                                                borderRadius: '8px',
                                                borderColor: 'rgba(40, 84, 161, 0.2)',
                                                bgcolor: 'rgba(40, 84, 161, 0.02)',
                                                color: '#2854a1',
                                                px: 0.5,
                                                py: 2,
                                                '& .MuiChip-label': { fontWeight: 500, fontSize: '0.85rem' },
                                                '& .MuiChip-icon': { color: '#2854a1' },
                                                '&:hover': {
                                                    bgcolor: 'rgba(40, 84, 161, 0.05)',
                                                    borderColor: '#2854a1'
                                                }
                                            }} 
                                        />
                                    ))}
                                </Box>
                            </Paper>
                        )}

                        {servicePrices.length > 0 && (
                            <Paper sx={sectionPaperStyle}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2, color: '#2854a1', fontSize: '1.1rem' }}>
                                    قیمت خدمات/محصولات
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {servicePrices.map((item, index) => (
                                        <Box
                                            key={`service-price-${index}`}
                                            sx={{
                                                p: 1.5,
                                                borderRadius: '8px',
                                                bgcolor: '#fffaf0',
                                                border: '1px solid #f1d7a8',
                                                color: '#6f4a13',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                lineHeight: 1.6,
                                                textAlign: textAlignFor(item),
                                                direction: textDirectionFor(item),
                                            }}
                                        >
                                            {item}
                                        </Box>
                                    ))}
                                </Box>
                            </Paper>
                        )}


                        {/* Menu Box */}
                        {(() => {
                            const isMenuEmpty = (menu: any) => {
                                if (isFieldEmpty(menu)) return true;
                                let parsedMenu = menu;
                                if (typeof menu === 'string') {
                                    try {
                                        parsedMenu = JSON.parse(menu);
                                    } catch (e) {
                                        return false; // If it's a non-JSON string and not empty string, it's not empty
                                    }
                                }
                                if (typeof parsedMenu === 'object' && parsedMenu !== null) {
                                    const hasSummary = !!(parsedMenu.summary && parsedMenu.summary.trim() !== '');
                                    const hasSections = Array.isArray(parsedMenu.sections) && parsedMenu.sections.length > 0;
                                    const hasSourceNotes = !!(parsedMenu.sourceNotes && parsedMenu.sourceNotes.trim() !== '');
                                    
                                    // If all these are false, the menu is effectively empty
                                    if (!hasSummary && !hasSections && !hasSourceNotes) {
                                        return true;
                                    }
                                }
                                return false;
                            };

                            if (isMenuEmpty(business.menu)) return null;

                            return (
                                <Paper sx={{ ...sectionPaperStyle, overflow: 'hidden' }}>
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3, color: '#2854a1' }}>
                                        Menu
                                    </Typography>
                                    
                                    {(() => {
                                        let parsed = business.menu;
                                        if (typeof parsed === 'string') {
                                            try {
                                                parsed = JSON.parse(parsed);
                                            } catch (e) {
                                                return (
                                                    <Box sx={{ 
                                                        whiteSpace: 'pre-wrap', 
                                                        color: '#717a8f', 
                                                        lineHeight: 1.7,
                                                        fontSize: '14px',
                                                        textAlign: /[\u0600-\u06FF]/.test(parsed) ? 'right' : 'left',
                                                        direction: /[\u0600-\u06FF]/.test(parsed) ? 'rtl' : 'ltr'
                                                    }}>
                                                        {parsed}
                                                    </Box>
                                                );
                                            }
                                        }

                                        if (typeof parsed === 'object' && parsed !== null) {
                                            return (
                                                <Box sx={{ 
                                                    bgcolor: '#fdfbf7', 
                                                    border: '1px solid #e8e3d8',
                                                    borderRadius: '12px',
                                                    p: { xs: 2, md: 4 },
                                                    boxShadow: 'inset 0 0 40px rgba(139, 90, 43, 0.03)'
                                                }}>
                                                    {parsed.summary && (
                                                        <Typography variant="body1" sx={{ mb: 4, fontStyle: 'italic', textAlign: 'center', color: '#6d5a49' }}>
                                                            {parsed.summary}
                                                        </Typography>
                                                    )}
                                                    
                                                    {Array.isArray(parsed.sections) && parsed.sections.map((section: any, idx: number) => (
                                                        <Box key={idx} sx={{ mb: 5 }}>
                                                            <Typography variant="h5" sx={{ 
                                                                fontFamily: '"Georgia", serif', 
                                                                color: '#2c1e16', 
                                                                borderBottom: '2px solid #e8e3d8',
                                                                pb: 1.5, 
                                                                mb: 3, 
                                                                textAlign: 'center',
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '1px'
                                                            }}>
                                                                {section.title || section.name || section.category || 'Menu'}
                                                            </Typography>
                                                            
                                                            <Grid container spacing={4}>
                                                                {Array.isArray(section.items) && section.items.map((item: any, itemIdx: number) => (
                                                                    <Grid size={{ xs: 12, md: 6 }} key={itemIdx}>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 0.5 }}>
                                                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2c1e16', fontSize: '1.05rem', lineHeight: 1.2 }}>
                                                                                {item.name || item.title || item.item}
                                                                            </Typography>
                                                                            {item.price && (
                                                                                <Box sx={{ display: 'flex', alignItems: 'flex-end', flexGrow: 1, ml: 2 }}>
                                                                                    <Box sx={{ flexGrow: 1, borderBottom: '2px dotted #d4cbb8', mx: 1, mb: 1 }}></Box>
                                                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#8b5a2b' }}>
                                                                                        {item.price}
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}
                                                                        </Box>
                                                                        {item.description && (
                                                                            <Typography variant="body2" sx={{ color: '#7a6a5a', fontStyle: 'italic', lineHeight: 1.5, mt: 0.5 }}>
                                                                                {item.description}
                                                                            </Typography>
                                                                        )}
                                                                    </Grid>
                                                                ))}
                                                            </Grid>
                                                        </Box>
                                                    ))}

                                                    {parsed.sourceNotes && (
                                                        <Typography variant="caption" sx={{ display: 'block', mt: 4, pt: 2, borderTop: '1px solid #e8e3d8', textAlign: 'center', color: '#999' }}>
                                                            {parsed.sourceNotes}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            );
                                        }

                                        return null;
                                    })()}
                                </Paper>
                            );
                        })()}

                        {/* Services & Features Box */}
                        {(services.length > 0 || features.length > 0) && (
                            <Paper sx={sectionPaperStyle}>
                                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3, color: '#2854a1' }}>
                                    Services & Features
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {services.length > 0 && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {services.map((service, index) => (
                                                <Box 
                                                    key={`service-${index}`} 
                                                    sx={{ 
                                                        bgcolor: '#eaf3ff', 
                                                        color: '#2854a1',
                                                        fontWeight: 500,
                                                        fontSize: '14px',
                                                        py: 1,
                                                        px: 2,
                                                        borderRadius: '8px'
                                                    }} 
                                                >
                                                    {service}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                    {features.length > 0 && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {features.map((feature, index) => (
                                                <Box 
                                                    key={`feature-${index}`} 
                                                    sx={{ 
                                                        bgcolor: '#f5f7fa', 
                                                        color: '#4a5568',
                                                        fontWeight: 500,
                                                        fontSize: '14px',
                                                        py: 1,
                                                        px: 2,
                                                        borderRadius: '8px'
                                                    }} 
                                                >
                                                    {feature}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            </Paper>
                        )}

                        {/* Policies & Other Useful Info Box */}
                        {(policies.length > 0 || otherUsefulInfo.length > 0) && (
                            <Paper sx={sectionPaperStyle}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                    {policies.length > 0 && (
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2854a1', mb: 1 }}>
                                                قوانین و مقررات
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                {policies.map((policy, index) => (
                                                    <Box
                                                        key={`policy-${index}`}
                                                        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
                                                    >
                                                        <PolicyIcon sx={{ fontSize: '1.1rem', color: '#2854a1', mt: '2px' }} />
                                                        <Typography variant="body2" sx={{ color: '#4a5568' }}>
                                                            {policy}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                    {otherUsefulInfo.length > 0 && (
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2854a1', mb: 1 }}>
                                                اطلاعات دیگر
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                {otherUsefulInfo.map((info, index) => (
                                                    <Box
                                                        key={`other-info-${index}`}
                                                        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
                                                    >
                                                        <InfoOutlinedIcon sx={{ fontSize: '1.1rem', color: '#2854a1', mt: '2px' }} />
                                                        <Typography variant="body2" sx={{ color: '#4a5568' }}>
                                                            {info}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </Paper>
                        )}

                        {/* Frequently asked questions (the website's own FAQ) */}
                        {faqItems.length > 0 && (
                            <Paper sx={sectionPaperStyle}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2854a1', mb: 1 }}>
                                    پرسش‌های متداول
                                </Typography>
                                {faqItems.map((item, index) => (
                                    <Accordion key={`faq-${index}`} disableGutters elevation={0}
                                        sx={{ border: '1px solid #e7eaee', '&:not(:last-of-type)': { borderBottom: 0 }, '&:before': { display: 'none' } }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#2d3748' }} dir="auto">{item.question}</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ pt: 0 }}>
                                            <Typography variant="body2" sx={{ color: '#4a5568', whiteSpace: 'pre-line' }} dir="auto">{item.answer}</Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Paper>
                        )}

                        {/* Mobile Gallery Box */}
                        {businessPhotoUrls.length > 0 && (
                            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                                {galleryBrowser(false)}
                            </Box>
                        )}

                        {/* Comments Section (desktop) */}
                        {isMdUp && commentsBlock}
                    </Grid>

                    {/* Right Column: Map & Contact Info */}
                    <Grid size={{ xs: 12, md: 4.5 }}>
                        {/* Wraps just the map/contact + Follow Us boxes so their
                            combined height can be compared against the description's -
                            see gallerySlot above. Working Hours and everything after
                            it is intentionally outside this wrapper. */}
                        <Box ref={sidebarTopRef}>
                        <Paper sx={{
                            ...sidebarPaperStyle,
                            p: 0,
                            mt: 0,
                            overflow: 'hidden',
                            '&:hover': {
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                            }
                        }}>
                            {/* Map Preview */}
                            <Box sx={{ width: '100%', height: 250, bgcolor: '#eee' }}>
                                <iframe
                                    title={`${business.name} map`}
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    loading="lazy"
                                    allowFullScreen
                                    src={`https://www.google.com/maps?q=${encodeURIComponent(business.address + ' ' + (business.city?.name || ''))}&output=embed`}
                                ></iframe>
                            </Box>

                            {/* Contact Info */}
                            <Box sx={{ p: 3 }}>
                                {keyPeople.length > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                                        <PersonIcon sx={{ mr: 2, color: '#2854a1', fontSize: 20, mt: 0.2 }} />
                                        <Box>
                                            {keyPeople.map((person: any, index: number) => (
                                                <Typography key={index} variant="body2" sx={{ color: '#717a8f', fontSize: '14px', mb: index < keyPeople.length - 1 ? 0.75 : 0 }}>
                                                    {person.role ? `${person.name} - ${person.role}` : person.name}
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: parsedAdditionalInfo?.address2 ? 2 : 3 }}>
                                    <LocationOnIcon sx={{ mr: 2, color: '#2854a1', fontSize: 20, mt: 0.2 }} />
                                    <Box>
                                        {parsedAdditionalInfo?.address2 && (
                                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#a0aabf', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>Main Address</Typography>
                                        )}
                                        <Typography variant="body2" sx={{ color: '#717a8f', fontSize: '14px', textAlign: 'left', direction: 'ltr' }}>
                                            {business.address}{business.city ? `, ${business.city.name}` : ''} {business.city?.state || ''}
                                        </Typography>
                                    </Box>
                                </Box>

                                {(googleMapsUrl || googleMapsNotFound) && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <DirectionsIcon sx={{ mr: 2, color: '#2854a1', fontSize: 20 }} />
                                        {googleMapsUrl ? (
                                            <Typography
                                                variant="body1"
                                                component="a"
                                                href={googleMapsUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ textDecoration: 'none', color: '#2854a1', fontSize: '14px', fontWeight: 600 }}
                                            >
                                                View on Google Maps
                                            </Typography>
                                        ) : (
                                            <Typography variant="body1" sx={{ color: '#717a8f', fontSize: '14px', fontWeight: 600 }}>
                                                Google Maps: Not Found
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                {parsedAdditionalInfo?.address2 && (
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                                        <LocationOnIcon sx={{ mr: 2, color: '#2854a1', fontSize: 20, mt: 0.2 }} />
                                        <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#a0aabf', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>Second Address</Typography>
                                            <Typography variant="body2" sx={{ color: '#717a8f', fontSize: '14px', textAlign: 'left', direction: 'ltr' }}>
                                                {parsedAdditionalInfo.address2}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}

                                {business.phone && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <PhoneIcon sx={{ mr: 2, color: '#2854a1', fontSize: 20 }} />
                                        <Typography variant="body1" sx={{ fontSize: '14px', color: '#2854a1' }}>{business.phone}</Typography>
                                    </Box>
                                )}

                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <EmailIcon sx={{ mr: 2, color: '#2854a1', fontSize: 20 }} />
                                    <Typography variant="body1" sx={{ wordBreak: 'break-all', fontSize: '14px', color: '#2854a1' }}>{business.companyEmail}</Typography>
                                </Box>

                                {business.website && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                        <LanguageIcon sx={{ mr: 2, color: '#2854a1', fontSize: 20 }} />
                                        <Typography
                                            variant="body1"
                                            component="a"
                                            href={websiteUrl(business.website) || undefined}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ textDecoration: 'none', color: '#2854a1', fontSize: '14px' }}
                                        >
                                            {business.website.replace(/^https?:\/\//, '')}
                                        </Typography>
                                    </Box>
                                )}

                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<DirectionsIcon />}
                                    href={googleMapsUrl || `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(business.address)}`}
                                    target="_blank"
                                    sx={{ py: 1.5, borderRadius: 2 }}
                                >
                                    Get Directions
                                </Button>
                            </Box>
                        </Paper>

                        {/* Social Media Box */}
                        {(socialUrl('facebook', business.facebook) || socialUrl('instagram', business.instagram) || socialUrl('telegram', business.telegram) || socialUrl('whatsapp', business.whatsapp)) && (
                            <Paper sx={sidebarPaperStyle}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Follow Us
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    {socialUrl('facebook', business.facebook) && (
                                        <IconButton
                                            component="a"
                                            href={socialUrl('facebook', business.facebook)!}
                                            rel="noopener noreferrer nofollow"
                                            target="_blank"
                                            sx={{ color: '#1877F2', '&:hover': { bgcolor: 'rgba(24, 119, 242, 0.1)' } }}
                                        >
                                            <FacebookIcon fontSize="large" />
                                        </IconButton>
                                    )}
                                    {socialUrl('instagram', business.instagram) && (
                                        <IconButton
                                            component="a"
                                            href={socialUrl('instagram', business.instagram)!}
                                            rel="noopener noreferrer nofollow"
                                            target="_blank"
                                            sx={{ color: '#E4405F', '&:hover': { bgcolor: 'rgba(228, 64, 95, 0.1)' } }}
                                        >
                                            <InstagramIcon fontSize="large" />
                                        </IconButton>
                                    )}
                                    {socialUrl('telegram', business.telegram) && (
                                        <IconButton
                                            component="a"
                                            href={socialUrl('telegram', business.telegram)!}
                                            rel="noopener noreferrer nofollow"
                                            target="_blank"
                                            sx={{ color: '#0088cc', '&:hover': { bgcolor: 'rgba(0, 136, 204, 0.1)' } }}
                                        >
                                            <TelegramIcon fontSize="large" />
                                        </IconButton>
                                    )}
                                    {socialUrl('whatsapp', business.whatsapp) && (
                                        <IconButton
                                            component="a"
                                            href={socialUrl('whatsapp', business.whatsapp)!}
                                            rel="noopener noreferrer nofollow"
                                            target="_blank"
                                            sx={{ color: '#25D366', '&:hover': { bgcolor: 'rgba(37, 211, 102, 0.1)' } }}
                                        >
                                            <WhatsAppIcon fontSize="large" />
                                        </IconButton>
                                    )}
                                </Box>
                            </Paper>
                        )}
                        </Box>

                        {gallerySlot === 'sidebar' && businessPhotoUrls.length > 0 && (
                            <Box sx={{ display: { xs: 'none', md: 'block' }, mb: 3 }}>
                                {galleryBrowser(true)}
                            </Box>
                        )}

                        {/* Working Hours Sidebar Box */}
                        {business.workingHours && Object.keys(business.workingHours).length > 0 && (
                            <Paper sx={sidebarPaperStyle}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <AccessTimeIcon sx={{ mr: 1, color: 'primary.main' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                        Working Hours
                                    </Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {(() => {
                                        const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                                        const dayToShort: Record<string, string> = {
                                            'Monday': 'mon', 'Tuesday': 'tue', 'Wednesday': 'wed', 
                                            'Thursday': 'thu', 'Friday': 'fri', 'Saturday': 'sat', 'Sunday': 'sun'
                                        };

                                        const schedules = days.map(day => {
                                            const shortKey = dayToShort[day];
                                            const rawKey = Object.keys(business.workingHours || {}).find(k => 
                                                k.toLowerCase() === day.toLowerCase() || k.toLowerCase() === shortKey
                                            );
                                            
                                            let h = rawKey ? (business.workingHours as any)[rawKey] : null;
                                            
                                            // Handle Array format [ { open, close } ] vs Object format { open, close }
                                            if (Array.isArray(h) && h.length > 0) {
                                                h = h[0];
                                            }

                                            if (!h) {
                                                return { day, isOpen: false, open: '', close: '' };
                                            }

                                            const openText = String(h.open || '');
                                            const closeText = String(h.close || '');
                                            const isExplicitlyClosed = h.isOpen === false || h.closed === true || h.status === 'closed';
                                            const isOpen = !isExplicitlyClosed &&
                                                Boolean(openText && closeText) &&
                                                openText.toLowerCase() !== 'closed' &&
                                                closeText.toLowerCase() !== 'closed';

                                            return { 
                                                day, 
                                                isOpen, 
                                                open: openText, 
                                                close: closeText 
                                            };
                                        });

                                        // Check for 24/7 or all days same
                                        const allOpen = schedules.every(s => s.isOpen);
                                        const first = schedules[0];
                                        const allSame = schedules.every(s => s.open === first.open && s.close === first.close);

                                        // Heuristic for 24/7 (00:00-23:59, 12:00 AM - 11:59 PM, or simply all days same and open)
                                        if (allOpen && allSame &&
                                            ((first.open === '00:00' && (first.close === '23:59' || first.close === '24:00' || first.close === '00:00')) ||
                                                (first.open === '12:00 AM' && first.close === '11:59 PM'))) {
                                            return (
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-start', py: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                                        Open 24/7
                                                    </Typography>
                                                </Box>
                                            );
                                        }

                                        // Consolidation logic
                                        const groups: { startDay: string; endDay: string; hours: string; isOpen: boolean }[] = [];
                                        let currentGroup: any = null;

                                        schedules.forEach((s, idx) => {
                                            const hourStr = s.isOpen ? `${s.open} - ${s.close}` : 'Closed';
                                            if (!currentGroup) {
                                                currentGroup = { startDay: s.day, endDay: s.day, hours: hourStr, isOpen: s.isOpen };
                                            } else if (currentGroup.hours === hourStr) {
                                                currentGroup.endDay = s.day;
                                            } else {
                                                groups.push(currentGroup);
                                                currentGroup = { startDay: s.day, endDay: s.day, hours: hourStr, isOpen: s.isOpen };
                                            }
                                            if (idx === schedules.length - 1) {
                                                groups.push(currentGroup);
                                            }
                                        });

                                        return groups.map((g, idx) => (
                                            <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, borderBottom: '1px solid #f9f9f9' }}>
                                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '14px' }}>
                                                    {g.startDay === g.endDay ? g.startDay : `${g.startDay} - ${g.endDay}`}
                                                </Typography>
                                                <Typography variant="body2" color={g.isOpen ? 'text.secondary' : 'error'} sx={{ fontSize: '14px' }}>
                                                    {g.hours}
                                                </Typography>
                                            </Box>
                                        ));
                                    })()}
                                </Box>
                            </Paper>
                        )}

                        {/* Comments Section (mobile) - after Working Hours */}
                        {!isMdUp && commentsBlock}
                    </Grid>
                </Grid>

                {/* Similar Listings Section */}
                {similarBusinesses.length > 0 && (
                    <Box sx={{ mt: 8 }}>
                        <Typography 
                            variant="h5" 
                            gutterBottom 
                            sx={{ 
                                fontWeight: 'bold', 
                                mb: 4, 
                                textAlign: 'right', 
                                color: '#717a8f',
                                borderBottom: '2px solid rgba(0,0,0,0.05)',
                                pb: 1
                            }}
                        >
                            موارد مشابه
                        </Typography>
                        <Grid container spacing={3}>
                            {similarBusinesses.map((sim) => (
                                <Grid key={sim.id} size={{ xs: 12, sm: 6, md: 3 }}>
                                    <BusinessCard business={sim} />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
            </Container>

            {/* Lightbox Dialog */}
            <Dialog
                open={!!lightboxImage}
                onClose={() => setLightboxImage(null)}
                maxWidth="lg"
                PaperProps={{
                    sx: { bgcolor: 'transparent', boxShadow: 'none' }
                }}
            >
                <DialogContent sx={{ p: 0, position: 'relative' }}>
                    <IconButton
                        onClick={() => setLightboxImage(null)}
                        sx={{ position: 'absolute', right: 16, top: 16, color: 'white', bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                    >
                        <CancelIcon />
                    </IconButton>
                    {businessPhotoUrls.length > 1 && (
                        <>
                            <IconButton
                                onClick={showPreviousPhoto}
                                sx={{
                                    position: 'absolute',
                                    left: 16,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: 'white',
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                    zIndex: 2,
                                }}
                            >
                                <ChevronLeftIcon />
                            </IconButton>
                            <IconButton
                                onClick={showNextPhoto}
                                sx={{
                                    position: 'absolute',
                                    right: 16,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: 'white',
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                    zIndex: 2,
                                }}
                            >
                                <ChevronRightIcon />
                            </IconButton>
                            <Chip
                                label={`${lightboxIndex + 1} / ${businessPhotoUrls.length}`}
                                size="small"
                                sx={{
                                    position: 'absolute',
                                    left: '50%',
                                    bottom: 16,
                                    transform: 'translateX(-50%)',
                                    bgcolor: 'rgba(0,0,0,0.58)',
                                    color: 'white',
                                    borderRadius: '4px',
                                    zIndex: 2,
                                }}
                            />
                        </>
                    )}
                    <Box
                        component="img"
                        src={lightboxImage || ''}
                        sx={{
                            maxWidth: '100%',
                            maxHeight: '90vh',
                            borderRadius: 1,
                            display: 'block'
                        }}
                    />
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default BusinessDetailsPage;

